// Leagues admin — Phase 1a: CRUD only.
//
// Registration (1b) and teams (1c) are not built yet. The two collections they
// will use, leagueRegistrations and leagueTeams, already have rules and are
// counted here, so the table shows zeroes rather than nothing once they land.
//
// Lives on its own route under /tournament-admin like Divisions and
// Registrations, rather than as a tab on the dashboard — leagues are run by
// the same people under the same tournament_admin gate.
import React, { useEffect, useState, useCallback } from 'react';
import {
  collection, query, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc,
  doc, where, getCountFromServer, getDocs, serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import AdultLeagueInterestList from '../components/AdultLeagueInterestList';
import {
  Box, Container, Typography, Button, Chip, CircularProgress, Alert,
  Table, TableContainer, TableBody, TableCell, TableHead, TableRow, Paper,
  IconButton, Tooltip, Grid, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, InputAdornment, FormControlLabel, Checkbox,
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PeopleIcon from '@mui/icons-material/People';
import GroupsIcon from '@mui/icons-material/Groups';
import EventIcon from '@mui/icons-material/Event';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNavigate } from 'react-router-dom';
import { dateStr } from '../utils/dates';
import {
  MATCH_FORMATS, DEFAULT_MATCH_FORMAT, describeLeagueFormat,
  resolveMatchFormat, capOver,
} from '../utils/leagueFormat';
import { loadAcceptableDocs } from '../utils/waiver';
import { SHARE_ORIGIN, copyText } from '../utils/shareLink';
import {
  LEAGUE_TYPES, LEAGUE_TYPE_HELP, LEAGUE_TYPE_LABEL, isJuniorType, leagueTypeOf,
} from '../utils/leagueTypes';
import { releaseLeagueCourts } from '../utils/eventCourtHolds';

// 'canceled' spelled the way the rest of the data spells it (tournaments use
// canceled-weather / canceled-lowreg), so anything matching on the word agrees.
// Setting it releases the league's court holds — see save() — and the public
// pages drop it on their own, since Leagues, Landing and the dashboard all
// whitelist registration/active/playoffs. Registration is refused server-side
// for the same reason: assertLeagueOpen requires status 'registration'.
const STATUSES = ['draft', 'registration', 'active', 'playoffs', 'complete', 'canceled'];
const STATUS_COLOR = {
  draft: 'default', registration: 'info', active: 'success',
  playoffs: 'warning', complete: 'secondary', canceled: 'error',
};
// Players per side. 6s is a full indoor-sized team on sand — rare, but the
// club runs it, and a format that cannot be picked is a league that has to be
// mislabelled as something else.
const FORMATS = [2, 3, 4, 5, 6];
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const COURTS = ['court-1', 'court-2', 'court-3', 'court-4', 'court-5', 'court-6'];
const courtLabel = (c) => `Court ${String(c).replace('court-', '')}`;

/** "18:30" -> "6:30 PM" */
const fmtTime = (t) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(t || ''));
  if (!m) return '';
  const h = Number(m[1]), mm = m[2];
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${mm} ${ampm}`;
};

// Full names, because pluralising the abbreviation gives "Fris" and "Thus".
const DAYS_FROM_INDEX = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_PLURAL = {
  Mon: 'Mondays', Tue: 'Tuesdays', Wed: 'Wednesdays', Thu: 'Thursdays',
  Fri: 'Fridays', Sat: 'Saturdays', Sun: 'Sundays',
};

/** Human summary of days + slots, used when no override is typed. */
const describeSchedule = (days, slots) => {
  const d = (days || []).length
    ? DAYS.filter(x => days.includes(x)).map(x => DAY_PLURAL[x] || x).join(' & ')
    : '';
  const t = (slots || []).filter(s2 => s2.startTime).map(s2 => fmtTime(s2.startTime)).join(', ');
  return [d, t].filter(Boolean).join(' ');
};

// A YYYY-MM-DD string is parsed as LOCAL midnight. new Date('2026-09-04')
// parses as UTC and lands on the previous day west of Greenwich, which would
// shift every season date by one.
const parseLocal = (iso) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(iso || ''))) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const addDays = (iso, days) => {
  const d = parseLocal(iso);
  if (!d) return '';
  d.setDate(d.getDate() + days);
  return dateStr(d);
};

/** Last regular-season night: numWeeks nights starting at startDate, weekly. */
const lastRegularWeek = (startDate, numWeeks) => {
  const n = Number(numWeeks) || 0;
  if (!parseLocal(startDate) || n < 1) return '';
  return addDays(startDate, (n - 1) * 7);
};

const fmtDate = (iso) => {
  const d = parseLocal(iso);
  if (!d) return '–';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const emptyForm = {
  name: '', format: 4, rosterMax: 4, startDate: '', numWeeks: 7,
  playoffDate: '', priceDollars: '', memberPriceDollars: '', maxTeams: '', registrationOpen: '',
  days: [], timeSlots: [{ startTime: '18:30', lengthMinutes: 60 }],
  registrationClose: '', dayTimeDisplay: '', status: 'draft', freeForActiveJuniors: false,
  // One set to 21, uncapped — what every league ran before these were settable.
  matchFormat: DEFAULT_MATCH_FORMAT, capOver: '',
  // '' = use the club default waiver.
  waiverDocId: '',
  // Junior league: sign-up asks for a parent/guardian and the player's date of
  // birth. A property of the league, so the question is settled before anyone
  // registers rather than inferred from whoever turns up.
  // Who the league is for. Separate from `juniors`, which is about age and
  // decides whether sign-up asks for a guardian and a date of birth — a junior
  // league can be coed, boys or girls just as an adult one can.
  leagueType: 'coed',
  // Which courts the league runs on. Two is what fixture generation assumed
  // before this was settable, so it stays the default.
  courts: ['court-1', 'court-2'],
  // Share link. Derived from the name when blank, and only ever derived at save
  // time — recomputing it on every keystroke would silently break a link
  // already handed out the moment someone tidied up a league's name.
  slug: '',
};

// A URL-safe slug from free text. Shared by the auto-fill and the save, so a
// slug typed by hand and one derived from the name are normalised identically.

export const toSlug = (v) => String(v || '').toLowerCase().trim()
  .replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');


export default function LeaguesAdmin() {
  const navigate = useNavigate();
  const [leagues, setLeagues] = useState([]);
  const [counts, setCounts] = useState({});      // leagueId -> { players, teams }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Which row last had its link copied, so the icon can confirm silently
  // instead of firing a snackbar for something this small.
  const [copiedId, setCopiedId] = useState(null);

  const copyShareLink = async (l) => {
    const url = `${SHARE_ORIGIN}/l/${l.slug || toSlug(l.name)}`;
    if (await copyText(url)) {
      setCopiedId(l.id);
      setTimeout(() => setCopiedId(c => (c === l.id ? null : c)), 2000);
    } else {
      setError(`Couldn't copy automatically — the link is ${url}`);
    }
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [waiverDocs, setWaiverDocs] = useState([]);
  useEffect(() => { loadAcceptableDocs().then(setWaiverDocs); }, []);

  useEffect(() => {
    const q = query(collection(db, 'leagues'), orderBy('startDate', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setLeagues(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (e) => {
      console.error('Leagues subscribe failed', e);
      setError('Could not load leagues: ' + e.message);
      setLoading(false);
    });
    return unsub;
  }, []);

  // Aggregate counts rather than reading every registration — a full league is
  // hundreds of docs and this table only ever shows the number. Single-field
  // where clauses, so no composite index is required.
  const refreshCounts = useCallback(async (ids) => {
    const next = {};
    for (const id of ids) {
      try {
        const [p, t] = await Promise.all([
          getCountFromServer(query(collection(db, 'leagueRegistrations'), where('leagueId', '==', id))),
          getCountFromServer(query(collection(db, 'leagueTeams'), where('leagueId', '==', id))),
        ]);
        next[id] = { players: p.data().count, teams: t.data().count };
      } catch (e) {
        // Collections may not exist until Phase 1b/1c — show a dash, not a crash.
        next[id] = { players: null, teams: null };
      }
    }
    setCounts(next);
  }, []);

  useEffect(() => {
    if (!leagues.length) return;
    refreshCounts(leagues.map(l => l.id));
  }, [leagues, refreshCounts]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFieldErrors({});
    setDialogOpen(true);
  };

  const openEdit = (l) => {
    setEditingId(l.id);
    setForm(formFromLeague(l));
    setFieldErrors({});
    setDialogOpen(true);
  };

  // A new league with this one's settings. Everything that belongs to the old
  // season is cleared — its name and share link, its dates and its status — so
  // the form refuses to save until the new season's dates are filled in.
  // Registrations, teams, fixtures and standings are never copied.
  const openDuplicate = (l) => {
    setEditingId(null);
    setForm({
      ...formFromLeague(l),
      name: `${l.name || ''} (copy)`,
      slug: '',
      startDate: '',
      playoffDate: '',
      registrationOpen: '',
      registrationClose: '',
      status: 'draft',
    });
    setFieldErrors({});
    setDialogOpen(true);
  };

  const formFromLeague = (l) => ({
      name: l.name || '',
      slug: l.slug || toSlug(l.name),
      // Leagues saved before types existed carry only the boolean, so read the
      // type back out of it rather than defaulting a junior league to adult.
      leagueType: leagueTypeOf(l),
      format: Number(l.format) || 4,
      rosterMax: Number(l.rosterMax) || Number(l.format) || 4,
      startDate: l.startDate || '',
      numWeeks: Number(l.numWeeks) || 7,
      playoffDate: l.playoffDate || '',
      // Stored in cents; the form works in dollars.
      priceDollars: l.price != null ? String(Number(l.price) / 100) : '',
      memberPriceDollars: l.memberPrice ? String(Number(l.memberPrice) / 100) : '',
      maxTeams: l.maxTeams != null ? String(l.maxTeams) : '',
      days: Array.isArray(l.days) ? l.days : [],
      timeSlots: Array.isArray(l.timeSlots) && l.timeSlots.length
        ? l.timeSlots.map(t => ({ startTime: t.startTime || '', lengthMinutes: Number(t.lengthMinutes) || 60 }))
        : [{ startTime: '18:30', lengthMinutes: 60 }],
      registrationOpen: l.registrationOpen || '',
      registrationClose: l.registrationClose || '',
      dayTimeDisplay: l.dayTimeDisplay || '',
      status: l.status || 'draft',
      // Leagues created before these existed really were one set to 21, so the
      // defaults describe them accurately rather than guessing.
      // Older leagues hold sets/playTo instead of an id; resolve maps them on.
      matchFormat: resolveMatchFormat(l).id || DEFAULT_MATCH_FORMAT,
      // capOver() converts a legacy absolute cap, so an old league opens
      // showing the offset it always effectively had.
      capOver: capOver(l) ? String(capOver(l)) : '',
      waiverDocId: l.waiverDocId || '',
      courts: Array.isArray(l.courts) && l.courts.length ? l.courts : ['court-1', 'court-2'],
  });

  const update = (k, v) => setForm(f => {
    const next = { ...f, [k]: v };
    // A roster can exceed the format (subs) but never fall below it — a 4s
    // league cannot field a roster of 3.
    if (k === 'format') {
      const fmt = Number(v) || 0;
      if ((Number(next.rosterMax) || 0) < fmt) next.rosterMax = fmt;
    }
    // Suggest the playoff night one week after the last regular week, but only
    // as a default — an admin who has typed their own date keeps it.
    if (k === 'startDate' || k === 'numWeeks') {
      const last = lastRegularWeek(k === 'startDate' ? v : next.startDate,
        k === 'numWeeks' ? v : next.numWeeks);
      const prevLast = lastRegularWeek(f.startDate, f.numWeeks);
      const untouched = !f.playoffDate || f.playoffDate === addDays(prevLast, 7);
      if (last && untouched) next.playoffDate = addDays(last, 7);
    }
    return next;
  });

  const toggleDay = (d) => setForm(f => ({
    ...f, days: f.days.includes(d) ? f.days.filter(x => x !== d) : [...f.days, d],
  }));
  const toggleCourt = (c) => setForm(f => ({
    ...f, courts: f.courts.includes(c) ? f.courts.filter(x => x !== c) : [...f.courts, c],
  }));
  const setSlot = (i, k, v) => setForm(f => ({
    ...f, timeSlots: f.timeSlots.map((t, idx) => idx === i ? { ...t, [k]: v } : t),
  }));
  const addSlot = () => setForm(f => ({
    ...f, timeSlots: [...f.timeSlots, { startTime: '', lengthMinutes: 60 }],
  }));
  const removeSlot = (i) => setForm(f => ({
    ...f, timeSlots: f.timeSlots.filter((_, idx) => idx !== i),
  }));

  const lastWeek = lastRegularWeek(form.startDate, form.numWeeks);

  // One match can be played per day, per slot, per court, each week.
  const weeklySlots = form.days.length * form.timeSlots.filter(t => t.startTime).length * form.courts.length;
  // A round-robin pairs everyone off, so a full week is half the teams.
  const matchesPerWeek = Math.floor((Number(form.maxTeams) || 0) / 2);
  const weekShortfall = matchesPerWeek > 0 && weeklySlots > 0 && weeklySlots < matchesPerWeek;

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!form.startDate) e.startDate = 'Required';
    if (!form.playoffDate) e.playoffDate = 'Required';
    else if (lastWeek && form.playoffDate <= lastWeek) {
      // String compare is safe and correct for YYYY-MM-DD.
      e.playoffDate = `Must be after the last regular week (${fmtDate(lastWeek)})`;
    }
    if ((Number(form.rosterMax) || 0) < (Number(form.format) || 0)) {
      e.rosterMax = `Cannot be below the format (${form.format})`;
    }
    const dollars = parseFloat(form.priceDollars);
    if (form.priceDollars !== '' && (isNaN(dollars) || dollars < 0)) e.priceDollars = 'Must be 0 or more';
    const mDollars = parseFloat(form.memberPriceDollars);
    if (form.memberPriceDollars !== '' && (isNaN(mDollars) || mDollars < 0)) {
      e.memberPriceDollars = 'Must be 0 or more';
    } else if (form.memberPriceDollars !== '' && !isNaN(dollars) && mDollars > dollars) {
      e.memberPriceDollars = 'Member price is above the standard price';
    }
    if (form.registrationOpen && form.registrationClose &&
        form.registrationClose < form.registrationOpen) {
      e.registrationClose = 'Cannot be before registration opens';
    }
    if ((Number(form.numWeeks) || 0) < 1) e.numWeeks = 'At least 1';
    // An offset of 0 would end a set the moment the goal is reached, which is
    // "no win by 2" rather than a cap, and negatives are meaningless.
    if (form.capOver !== '' && (Number(form.capOver) || 0) < 1) {
      e.capOver = 'At least 1 — leave blank for no cap';
    }
    if (form.maxTeams !== '' && (Number(form.maxTeams) || 0) < 0) e.maxTeams = 'Must be 0 or more';
    if (!form.days.length) e.days = 'Pick at least one day';
    else if (form.startDate) {
      // The season is laid out week by week from startDate, so a start that
      // is not one of the league's own nights pushes week 1 onto the wrong
      // date entirely.
      const wd = DAYS_FROM_INDEX[parseLocal(form.startDate)?.getDay()];
      if (wd && !form.days.includes(wd)) {
        e.startDate = `${fmtDate(form.startDate)} is a ${DAY_PLURAL[wd].slice(0, -1)} — pick a day the league runs`;
      }
    }
    if (!form.courts.length) e.courts = 'Pick at least one court';
    if (!form.timeSlots.some(t => t.startTime)) e.timeSlots = 'Add at least one time slot';
    if (form.timeSlots.some(t => t.startTime && (Number(t.lengthMinutes) || 0) < 1)) {
      e.timeSlots = 'Every slot needs a length in minutes';
    }
    setFieldErrors(e);
    return Object.keys(e).length === 0;
  };

  // What cancelling is about to do to people, counted before it is done.
  //
  // Cancelling a league releases its court holds and nothing else: no refunds
  // are initiated, nobody is told, and every registration stays paid. Those are
  // reasonable defaults — whether to refund in full, in part, or as credit is a
  // judgement — but they are quiet ones, and the money should be in front of
  // whoever is clicking rather than discovered later.
  const cancelImpact = async (leagueId) => {
    const [resSnap, regSnap] = await Promise.all([
      getDocs(query(collection(db, 'reservations'), where('leagueId', '==', leagueId))),
      getDocs(query(collection(db, 'leagueRegistrations'), where('leagueId', '==', leagueId))),
    ]);
    const live = regSnap.docs.map(d => d.data()).filter(r => r.status !== 'cancelled');
    const paidCents = live.reduce((sum, r) => sum + (Number(r.amountPaid) || 0), 0);
    return {
      holds: resSnap.size,
      players: live.length,
      paidPlayers: live.filter(r => (Number(r.amountPaid) || 0) > 0).length,
      paidCents,
      owed: live.filter(r => r.paymentStatus === 'pending').length,
    };
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true); setError('');
    try {
      const dollars = parseFloat(form.priceDollars);
      const data = {
        name: form.name.trim(),
        // Falls back to the name so every league has a usable link even if the
        // field is never touched; normalised either way so a typed slug can't
        // carry spaces or punctuation into a URL.
        slug: toSlug(form.slug) || toSlug(form.name),
        leagueType: form.leagueType || 'coed',
        // Derived, never set by hand: the dropdown is the only control, and a
        // boolean that could disagree with it is a boolean that eventually will.
        juniors: isJuniorType(form.leagueType),
        format: Number(form.format),
        rosterMax: Number(form.rosterMax),
        startDate: form.startDate,
        numWeeks: Number(form.numWeeks),
        playoffDate: form.playoffDate,
        // Money is stored in cents so no float ever reaches Firestore.
        price: isNaN(dollars) ? 0 : Math.round(dollars * 100),
        // Blank/0 = no member rate, not free for members.
        memberPrice: form.memberPriceDollars === '' ? 0
          : Math.round((parseFloat(form.memberPriceDollars) || 0) * 100),
        // 0 / blank = uncapped. The player cap is derived from this and
        // rosterMax; beyond it, registrations are wait-listed.
        maxTeams: form.maxTeams === '' ? 0 : (Number(form.maxTeams) || 0),
        days: DAYS.filter(d => form.days.includes(d)),   // stored in week order
        timeSlots: form.timeSlots
          .filter(t => t.startTime)
          .map(t => ({ startTime: t.startTime, lengthMinutes: Number(t.lengthMinutes) || 60 })),
        // Falls back to a summary of days + slots so the two can't disagree.
        dayTimeDisplay: form.dayTimeDisplay.trim()
          || describeSchedule(form.days, form.timeSlots),
        registrationOpen: form.registrationOpen || '',
        registrationClose: form.registrationClose || '',
        status: form.status,
        freeForActiveJuniors: form.freeForActiveJuniors === true,
        matchFormat: form.matchFormat || DEFAULT_MATCH_FORMAT,
        // sets/playTo are written alongside the id, derived from it. They are
        // what anything reading a league directly — an export, a report, a
        // query — has always found there, and dropping them silently would
        // change what those see. playTo is the opening set: on 21/21/15 the
        // decider is shorter, which only the targets array can say.
        ...(() => {
          const t = resolveMatchFormat({ matchFormat: form.matchFormat }).targets;
          return { sets: t.length, playTo: t[0], setTargets: t };
        })(),
        // 0 = no cap, the same convention maxTeams uses for "no limit". Blank
        // and 0 both mean win-by-2 runs on with no ceiling.
        capOver: form.capOver === '' ? 0 : (Number(form.capOver) || 0),
        // The absolute cap of the opening set, written alongside for anything
        // reading a league doc directly — the same reason sets/playTo are kept.
        cap: form.capOver === ''
          ? 0
          : resolveMatchFormat({ matchFormat: form.matchFormat }).targets[0]
            + (Number(form.capOver) || 0),
        // Blank means "use the club default", resolved at registration time so
        // a league does not freeze whichever waiver happened to be current when
        // it was created.
        waiverDocId: form.waiverDocId || '',
        courts: COURTS.filter(c => form.courts.includes(c)),   // stored in court order
        updatedAt: serverTimestamp(),
      };
      if (editingId) {
        // Calling a league off has to let its courts go, or the calendar stays
        // blocked for matches nobody will play — the same gap a cancelled
        // tournament had. The fixtures stay: they are the record of what was
        // scheduled, and only the holds block anyone else.
        const wasCanceled = leagues.find(l => l.id === editingId)?.status === 'canceled';
        const nowCanceled = form.status === 'canceled';
        if (nowCanceled && !wasCanceled) {
          const i = await cancelImpact(editingId);
          const money = i.paidCents > 0
            ? `${i.paidPlayers} of them have paid $${(i.paidCents / 100).toFixed(2)} in total, and NOBODY IS REFUNDED by cancelling — refunds are yours to make in Stripe.`
            : 'Nobody has paid anything.';
          const ok = window.confirm(
            `Cancel "${form.name}"?\n\n`
            + `${i.holds} court hold(s) will be released from the calendar.\n`
            + `${i.players} registration(s) stay on the league as they are.\n`
            + `${money}\n`
            + (i.owed ? `${i.owed} still owe — they will not be chased.\n` : '')
            + `\nNobody is told. The league disappears from the public pages and no new sign-ups are accepted.`);
          if (!ok) { setSaving(false); return; }
        }
        await updateDoc(doc(db, 'leagues', editingId), data);
        let freed = 0;
        if (nowCanceled && !wasCanceled) {
          try {
            freed = await releaseLeagueCourts(db, editingId);
          } catch (e) {
            console.error('Release league courts failed', e);
            setError('The league was cancelled, but its court holds could not be released: ' + e.message);
          }
        }
        setSuccess(
          nowCanceled && !wasCanceled
            ? `League cancelled${freed ? `, and ${freed} court hold(s) released` : ''}.`
            : wasCanceled && !nowCanceled
              ? 'League reopened. Its court holds were released when it was cancelled — rebuild them from the schedule.'
              : 'League updated.');
      } else {
        await addDoc(collection(db, 'leagues'), {
          ...data,
          createdAt: serverTimestamp(),
          createdBy: auth.currentUser?.uid || '',
        });
        setSuccess('League created.');
      }
      setTimeout(() => setSuccess(''), 4000);
      setDialogOpen(false);
    } catch (e) {
      console.error('Save league failed', e);
      setError('Save failed: ' + e.message);
    }
    setSaving(false);
  };

  // Deleting a league with people in it would orphan their registrations and
  // whatever they paid. Only an untouched draft can go; anything else is
  // retired by setting status to complete.
  const remove = async (l) => {
    const c = counts[l.id];
    if (l.status !== 'draft') {
      setError(`Only a draft league can be deleted. Set "${l.name}" to complete instead.`);
      return;
    }
    if (c && c.players > 0) {
      setError(`"${l.name}" has ${c.players} registration(s) and cannot be deleted. Set it to complete instead.`);
      return;
    }
    if (!window.confirm(`Delete "${l.name}"? This cannot be undone.`)) return;
    try {
      // A draft can still have had a schedule built, and each fixture holds a
      // court through a reservations row. Deleting the league without those
      // leaves the calendar blocked for matches that no longer exist — the
      // same gap cancelling a tournament had.
      const freed = await releaseLeagueCourts(db, l.id);
      await deleteDoc(doc(db, 'leagues', l.id));
      setSuccess(freed ? `League deleted, and ${freed} court hold(s) released.` : 'League deleted.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      console.error('Delete league failed', e);
      setError('Delete failed: ' + e.message);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/tournament-admin')}><ArrowBackIcon /></IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight="bold">Leagues</Typography>
          <Typography variant="body2" color="text.secondary">
            {leagues.length} league{leagues.length === 1 ? '' : 's'}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddCircleIcon />} onClick={openCreate}>
          Add League
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <AdultLeagueInterestList />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : leagues.length === 0 ? (
        <Paper elevation={1} sx={{ p: 5, textAlign: 'center', borderRadius: 2, border: '1px dashed #ccc' }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>No leagues yet.</Typography>
          <Button variant="contained" onClick={openCreate}>Add One</Button>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>League</strong></TableCell>
                <TableCell><strong>Format</strong></TableCell>
                <TableCell><strong>Season</strong></TableCell>
                <TableCell><strong>Playoffs</strong></TableCell>
                <TableCell align="right"><strong>Price</strong></TableCell>
                <TableCell align="right"><strong>Players</strong></TableCell>
                <TableCell align="right"><strong>Teams</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leagues.map(l => {
                const c = counts[l.id];
                const last = lastRegularWeek(l.startDate, l.numWeeks);
                return (
                  <TableRow key={l.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{l.name}</Typography>
                      {l.dayTimeDisplay && (
                        <Typography variant="caption" color="text.secondary">{l.dayTimeDisplay}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {l.format}s
                      {Number(l.rosterMax) > Number(l.format) && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          roster {l.rosterMax}
                        </Typography>
                      )}
                      {/* Who the league is for, in place of the scoring line.
                          The scoring rules are still set and shown in the edit
                          dialog; at a glance across the list, "Junior Girls"
                          tells you which league this is and "21/21/15, win by
                          2, cap 23/23/17" does not. */}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {LEAGUE_TYPE_LABEL[leagueTypeOf(l)]}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{fmtDate(l.startDate)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {l.numWeeks} week{Number(l.numWeeks) === 1 ? '' : 's'}
                        {last ? ` · ends ${fmtDate(last)}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>{fmtDate(l.playoffDate)}</TableCell>
                    <TableCell align="right">
                      ${((Number(l.price) || 0) / 100).toFixed(2)}
                      {Number(l.memberPrice) > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          ${(Number(l.memberPrice) / 100).toFixed(2)} member
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {c ? (c.players ?? '–') : '…'}
                      {Number(l.maxTeams) > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          of {Number(l.maxTeams) * (Number(l.rosterMax) || 0)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {c ? (c.teams ?? '–') : '…'}
                      {Number(l.maxTeams) > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          of {l.maxTeams}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={l.status || 'draft'} color={STATUS_COLOR[l.status] || 'default'} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={copiedId === l.id ? 'Link copied' : `Copy share link: /l/${l.slug || toSlug(l.name)}`}>
                        <IconButton size="small" color={copiedId === l.id ? 'success' : 'default'}
                          onClick={() => copyShareLink(l)}>
                          <LinkIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Registrations">
                        <IconButton size="small"
                          onClick={() => navigate(`/tournament-admin/leagues/${l.id}/registrations`)}>
                          <PeopleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Schedule">
                        <IconButton size="small"
                          onClick={() => navigate(`/tournament-admin/leagues/${l.id}/schedule`)}>
                          <EventIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Teams">
                        <IconButton size="small"
                          onClick={() => navigate(`/tournament-admin/leagues/${l.id}/teams`)}>
                          <GroupsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(l)}><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Duplicate">
                        <IconButton size="small" onClick={() => openDuplicate(l)}><ContentCopyIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title={l.status === 'draft' ? 'Delete' : 'Only drafts can be deleted'}>
                        <span>
                          <IconButton size="small" color="error" onClick={() => remove(l)}
                            disabled={l.status !== 'draft'}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Edit League' : 'New League'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {/* Two rows on desktop, everything stacked on a phone. These six
                fields define what the league IS; the scheduling below is a
                separate job, and keeping the identity above it in a fixed block
                means the dialog opens on the whole of one thought. */}
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth label="League Name *" value={form.name}
                onChange={e => update('name', e.target.value)}
                error={!!fieldErrors.name} helperText={fieldErrors.name || ' '} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField fullWidth select label="Status" value={form.status}
                onChange={e => update('status', e.target.value)} helperText=" ">
                {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              {/* How the first junior season is being run: the club's own
                  training juniors play for nothing. Decided per league, and
                  applied from the athlete record at sign-up, so nobody has to
                  remember to refund a junior afterwards. */}
              <FormControlLabel
                control={<Checkbox checked={form.freeForActiveJuniors === true}
                  onChange={e => update('freeForActiveJuniors', e.target.checked)} />}
                label="Free for active Torch juniors (a live training plan, or a check-in in the last 60 days)" />
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <TextField fullWidth select label="Team format" value={form.format}
                onChange={e => update('format', Number(e.target.value))}
                helperText="Per team">
                {FORMATS.map(f => <MenuItem key={f} value={f}>{f}s</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField fullWidth type="number" label="Roster Max" value={form.rosterMax}
                onChange={e => update('rosterMax', e.target.value)}
                error={!!fieldErrors.rosterMax}
                helperText={fieldErrors.rosterMax || 'Subs above the format'}
                inputProps={{ min: form.format, step: 1 }} />
            </Grid>

            {/* Two formats, two questions: how many players a side (above),
                and how the points are counted (here). Named apart so neither
                has to be read in the context of the other. */}
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth select label="Scoring format" value={form.matchFormat}
                onChange={e => update('matchFormat', e.target.value)}
                helperText="Sets and points per set">
                {MATCH_FORMATS.map(f => <MenuItem key={f.id} value={f.id}>{f.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <TextField fullWidth type="number" label="Cap" value={form.capOver}
                onChange={e => update('capOver', e.target.value)}
                error={!!fieldErrors.capOver}
                helperText={fieldErrors.capOver || 'Points past'}
                inputProps={{ min: 1, step: 1 }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField fullWidth select label="League type" value={form.leagueType || 'coed'}
                onChange={e => update('leagueType', e.target.value)}
                helperText={LEAGUE_TYPE_HELP[form.leagueType] || ' '}>
                {LEAGUE_TYPES.map(t => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                {describeLeagueFormat({ matchFormat: form.matchFormat, capOver: form.capOver })}
                {!form.capOver && ' — a set runs on until someone leads by two.'}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="caption" color={fieldErrors.courts ? 'error' : 'text.secondary'}>
                Courts *
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                {COURTS.map(c => {
                  const on = form.courts.includes(c);
                  return (
                    <Chip key={c} label={courtLabel(c)} clickable onClick={() => toggleCourt(c)}
                      color={on ? 'primary' : 'default'} variant={on ? 'filled' : 'outlined'} />
                  );
                })}
              </Box>
              {fieldErrors.courts && (
                <Typography variant="caption" color="error">{fieldErrors.courts}</Typography>
              )}
              {/* Days x slots x courts is how many matches a week can hold, and a
                  round-robin needs half the teams. Saying so here is cheaper than
                  finding out at fixture generation that a week does not fit. */}
              <Typography variant="caption" color={weekShortfall ? 'error' : 'text.secondary'}
                sx={{ display: 'block', mt: 0.5 }}>
                {weeklySlots} match{weeklySlots === 1 ? '' : 'es'} a week available
                {matchesPerWeek > 0 && ` · ${matchesPerWeek} needed for ${form.maxTeams || '?'} teams`}
                {weekShortfall ? ' — some fixtures will not fit' : ''}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField fullWidth select label="Waiver" value={form.waiverDocId}
                onChange={e => update('waiverDocId', e.target.value)}
                helperText="Blank uses the club's default. Only waivers a signed-out player can open are listed — league sign-up is public.">
                <MenuItem value="">Club default</MenuItem>
                {waiverDocs.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12 }}>
              {isJuniorType(form.leagueType) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Sign-up asks for a parent or guardian's name and the player's date of birth,
                  on both the league page and the public sign-up link. Pick the junior waiver above.
                </Typography>
              )}
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="caption" color={fieldErrors.days ? 'error' : 'text.secondary'}>
                Days of the week *
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                {DAYS.map(d => {
                  const on = form.days.includes(d);
                  return (
                    <Chip key={d} label={d} clickable onClick={() => toggleDay(d)}
                      color={on ? 'primary' : 'default'} variant={on ? 'filled' : 'outlined'} />
                  );
                })}
              </Box>
              {fieldErrors.days && (
                <Typography variant="caption" color="error">{fieldErrors.days}</Typography>
              )}
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="caption" color={fieldErrors.timeSlots ? 'error' : 'text.secondary'}>
                Time slots * — a start time and how long each slot runs
              </Typography>
              {form.timeSlots.map((t, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                  <TextField type="time" size="small" label="Start" value={t.startTime}
                    onChange={e => setSlot(i, 'startTime', e.target.value)}
                    InputLabelProps={{ shrink: true }} sx={{ minWidth: 130 }} />
                  <TextField type="number" size="small" label="Length" value={t.lengthMinutes}
                    onChange={e => setSlot(i, 'lengthMinutes', e.target.value)}
                    inputProps={{ min: 1, step: 5 }}
                    InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }}
                    sx={{ minWidth: 130 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                    {t.startTime ? `${fmtTime(t.startTime)} · ${t.lengthMinutes || 0} min` : ''}
                  </Typography>
                  <IconButton size="small" color="error" onClick={() => removeSlot(i)}
                    disabled={form.timeSlots.length === 1}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button size="small" startIcon={<AddCircleIcon />} onClick={addSlot} sx={{ mt: 1 }}>
                Add slot
              </Button>
              {fieldErrors.timeSlots && (
                <Typography variant="caption" color="error" sx={{ display: 'block' }}>{fieldErrors.timeSlots}</Typography>
              )}
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Display override" value={form.dayTimeDisplay}
                onChange={e => update('dayTimeDisplay', e.target.value)}
                placeholder={describeSchedule(form.days, form.timeSlots) || 'Fridays 6:30 PM'}
                helperText={`Leave blank to show: "${describeSchedule(form.days, form.timeSlots) || '—'}"`} />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth type="date" label="First Match Night *" value={form.startDate}
                onChange={e => update('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                error={!!fieldErrors.startDate} helperText={fieldErrors.startDate || ''} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth type="number" label="Weeks" value={form.numWeeks}
                onChange={e => update('numWeeks', e.target.value)}
                error={!!fieldErrors.numWeeks}
                helperText={fieldErrors.numWeeks || (lastWeek ? `Last regular week: ${fmtDate(lastWeek)}` : '')}
                inputProps={{ min: 1, step: 1 }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth type="date" label="Playoff Date *" value={form.playoffDate}
                onChange={e => update('playoffDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                error={!!fieldErrors.playoffDate}
                helperText={fieldErrors.playoffDate || (lastWeek ? `Must be after ${fmtDate(lastWeek)}` : '')} />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth type="number" label="Price per player" value={form.priceDollars}
                onChange={e => update('priceDollars', e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                inputProps={{ min: 0, step: 0.01 }}
                error={!!fieldErrors.priceDollars}
                helperText={fieldErrors.priceDollars || 'Per player, per season'} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth type="number" label="Member price" value={form.memberPriceDollars}
                onChange={e => update('memberPriceDollars', e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                inputProps={{ min: 0, step: 0.01 }}
                error={!!fieldErrors.memberPriceDollars}
                helperText={fieldErrors.memberPriceDollars || 'Blank = no member rate'} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth type="number" label="Max Teams" value={form.maxTeams}
                onChange={e => update('maxTeams', e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                error={!!fieldErrors.maxTeams}
                helperText={fieldErrors.maxTeams || (Number(form.maxTeams) > 0
                  ? `${Number(form.maxTeams) * (Number(form.rosterMax) || 0)} players · beyond this, wait-listed`
                  : 'Blank or 0 = uncapped')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth type="date" label="Registration Opens" value={form.registrationOpen}
                onChange={e => update('registrationOpen', e.target.value)}
                InputLabelProps={{ shrink: true }} helperText="Optional" />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth type="date" label="Registration Closes" value={form.registrationClose}
                onChange={e => update('registrationClose', e.target.value)}
                InputLabelProps={{ shrink: true }}
                error={!!fieldErrors.registrationClose}
                helperText={fieldErrors.registrationClose || 'Optional'} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="URL Slug" value={form.slug}
                onChange={e => update('slug', e.target.value)}
                placeholder={toSlug(form.name) || 'summer-4s'}
                InputLabelProps={{ shrink: true }}
                helperText={`Share link: ${SHARE_ORIGIN}/l/${toSlug(form.slug) || toSlug(form.name) || 'your-slug'}`
                  + (editingId ? ' — changing this breaks links already shared.' : ' — leave blank to use the name.')} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : (editingId ? 'Save Changes' : 'Create League')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
